exports.makeRequest = makeRequest;
exports.initSentry = initSentry;
exports.htmlSubstring = htmlSubstring;
exports.getCharCount = getCharCount;
exports.showExpandedState = showExpandedState;
exports.handleIsMobileDevice = handleIsMobileDevice;

import * as Sentry from "@sentry/browser";

const config = require("../../config");

function makeRequest(opts) {
  return new Promise(function (resolve, reject) {
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method, opts.url);
    xhr.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        resolve(xhr.response);
      } else {
        if (
          this.status == 503 ||
          (this.status == 403 &&
            xhr.response &&
            JSON.parse(xhr.response).code == "STALE_REQUEST")
        ) {
          window.location.reload();
        }
        reject({
          status: this.status,
          statusText: xhr.statusText,
          response: xhr.response,
        });
      }
    };
    xhr.onerror = function () {
      reject({
        status: this.status,
        statusText: xhr.statusText,
        response: xhr.response,
      });
    };
    if (opts.method === "POST" && !opts.headers.hasOwnProperty("csrf-token")) {
      const csrfToken = getCookie("csrf-token");
      csrfToken && xhr.setRequestHeader("csrf-token", csrfToken);
    }
    xhr.setRequestHeader(
      config.customReqHeaders.appVersion.key,
      config.customReqHeaders.appVersion.value
    );
    if (opts.headers) {
      Object.keys(opts.headers).forEach(function (key) {
        xhr.setRequestHeader(key, opts.headers[key]);
      });
    }
    if (opts.params) {
      let params = JSON.stringify(opts.params);
      xhr.send(params);
    } else {
      xhr.send();
    }
  });
}
function getCookie(name) {
  const regex = new RegExp("(^| )" + name + "=([^;]+)");
  const match = regex.exec(document.cookie);

  if (match) return match[2];
}

const ShortMonthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// rich text editor adds html tags in annoucement text .. para tag,
// strong etc, link tag etc etc . While char count, we need to remove
// these tags and then count the string length
function getCharCount(str) {
  let string = str.replace(/(<([^>]+)>)/gi, "");
  return string.length;
}

/**
 * Appends a "See more" link or ellipsis to the given string if there are multiple <p> elements in the provided document.
 *
 * @param {string} str - The string to modify.
 * @param {boolean} seeMore - Determines whether to append a "See more" link (true) or just an ellipsis (false).
 * @param {Document} doc - The parsed HTML document used to check for <p> elements.
 * @returns {string} The updated string with a "See more" link, ellipsis, or unchanged if only one <p> exists.
 */
function appendSeeMore(str, seeMore, doc) {
  if (doc.querySelectorAll("p").length > 1) { 
    if (seeMore) {
      return str + '...<a href="javascript:void(0)" class="announcement-toggle cursor-pointer"><span class="show-more">See more</span></a>';
    } else {
      return str + " ...";
    }
  }
  return str;
}

// method to trim the string till n characters. Note that the string to be trimmed comprises of HTML tags, hence we need to trim the plain string and preserve its tags too
function htmlSubstring(str, length, seeMore = false) {
  let charCount;

  // Parse the input string as HTML using DOMParser
  let doc = new DOMParser().parseFromString(str, "text/html");

  // If there are multiple <p> tags in the input string, only consider the content of the first <p> tag
  if (doc.querySelectorAll("p").length > 1) {
    str = "<p>" + doc.querySelector("p").innerHTML + "</p>";
  }

  // Get the character count of the input string without HTML tags
  charCount = getCharCount(str);

  // If the character count is less than or equal to the desired length, return the string as is
  if (charCount <= length) return appendSeeMore(str, seeMore, doc);

  // Calculate the number of <br> tags in the input string
  let breakPattern = /<br ?\/?>/g;
  let breakOcc = str.match(breakPattern) ? str.match(breakPattern).length : 0;

  // Consider <br> tags as characters and reduce the length based on the number of <br> tags
  length = length - breakOcc;

  let r = /<([^>\s]*)[^>]*>/g,
    stack = [],
    lasti = 0,
    result = "",
    m = r.exec(str);

  // For each HTML tag, while we don't have enough characters
  while (m && length) {
    // Get the text substring between the last tag and this one
    let temp = str.substring(lasti, m.index).substr(0, length);

    // Append the substring to the result and count the number of characters added
    result += temp;
    length -= temp.length;
    lasti = r.lastIndex;

    if (length) {
      result += m[0];
      if (m[1].startsWith("/")) {
        // If this is a closing tag, pop it from the stack (does not account for bad HTML)
        stack.pop();
      } else if (!m[1].endsWith("/")) {
        // If this is not a self-closing tag, push it into the stack
        stack.push(m[1]);
      }
    }
    m = r.exec(str);
  }

  if (seeMore) {
    result +=
      '...<a href="javascript:void(0)" class="announcement-toggle cursor-pointer"><span class="show-more">See more</span></a>';
  }
  // Fix any unclosed tags by appending their closing tags to the result
  while (stack.length) {
    result += "</" + stack.pop() + ">";
  }

  // Append ellipsis to indicate that the string has been trimmed
  if (!seeMore) {
    result += "...";
  }
  return result;
}

function showExpandedState(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  // Get all paragraph elements
  const paragraphs = doc.querySelectorAll("p");

  // Check if there are any paragraphs
  if (!paragraphs.length) {
    return htmlString; // Handle cases where no paragraphs are found
  }

  // Get the last paragraph element
  const lastParagraph = paragraphs[paragraphs.length - 1];

  const showLessLink = document.createElement("a");
  showLessLink.textContent = "See less";
  showLessLink.className = "show-less announcement-toggle cursor-pointer";
  showLessLink.href = "javascript:void(0)";

  // Append showLessLink to the last paragraph
  lastParagraph.appendChild(showLessLink);

  // Get the modified HTML string
  const modifiedHTML = doc.body.innerHTML;

  return modifiedHTML;
}

//checks if the device being used is mobile or not
function handleIsMobileDevice() {
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
}

function initSentry() {
  if (SENTRY_DSN && SENTRY_ENABLED && APP_ENV != "local") {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: APP_ENV.toUpperCase(),
    });
    Sentry.configureScope(function (scope) {
      scope.setTag("app_name", APP_NAME);
    });
  }
}
